import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Compass } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { ShareButton } from "@/components/ShareButton";
import { GygTourBand } from "@/components/GetYourGuide";
import { TRIPS } from "@/data/trips";

const SITE = "https://cphhighlights.com";
const HERO_IMG =
  "https://images.unsplash.com/photo-1720784043091-5d9e73f63609?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Day Trips from Copenhagen: Bakken, Louisiana, Malmö & More",
  url: `${SITE}/trips`,
  description:
    "The best day trips from Copenhagen — Bakken's deer forest, Louisiana's art by the sea, Hamlet's castle in Helsingør, Roskilde's Viking ships, Dragør's yellow cottages and Malmö across the bridge. Train times, tips and tours.",
  isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
  publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
};

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Day Trips from Copenhagen", item: `${SITE}/trips` },
  ],
};

export default function TripsPage() {
  return (
    <main data-testid="trips-page" className="pt-16 md:pt-20 bg-white text-black">
      <Seo
        title="Day Trips from Copenhagen: Bakken, Louisiana, Malmö & More"
        description="The best day trips from Copenhagen — Louisiana's art by the sea, Hamlet's castle in Helsingør, Roskilde's Viking ships, Dragør and Malmö, with train times."
        path="/trips"
        image={HERO_IMG}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBPAGE)}</script>
      </Helmet>

      {/* hero */}
      <section data-testid="trips-hero" className="relative overflow-hidden bg-[#13141A] text-white border-b border-white/20">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-10 pb-16 md:pb-24">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="trips-back"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton title="Day Trips from Copenhagen" testid="trips-share" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white">
              GET OUT OF TOWN
            </p>
            <h1 className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-3xl">
              Day trips from <span className="font-editorial italic font-600">Copenhagen</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/85 max-w-2xl mt-6">
              Louisiana, Hamlet&apos;s castle, deer forests and Viking ships — and Sweden itself.
              Every trip here is under an hour from the city centre. Bring your passport for Malmö.
            </p>
          </motion.div>
        </div>
      </section>

      {/* trip cards */}
      <section data-testid="trips-list" className="py-12 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...TRIPS].sort((a, b) => a.mins - b.mins).map((d, i) => (
              <Reveal key={d.name} delay={(i % 3) * 0.05}>
                <Link
                  to={`/trips/${d.id}`}
                  data-testid={`trips-card-${d.id}`}
                  className="group relative block border border-zinc-200 shadow-brut hover:shadow-brut-lg hover:-translate-y-1 transition-all overflow-hidden h-full min-h-[300px] text-white"
                >
                  {d.image ? (
                    <img
                      src={d.image}
                      alt={`${d.name} — a day trip from Copenhagen`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  ) : (
                    <div className="absolute inset-0" style={{ backgroundColor: d.accent }} />
                  )}
                  <div className="absolute inset-0 bg-black/45" />
                  <div className="relative p-6 flex flex-col h-full min-h-[300px]">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-body font-800 text-[11px] tracking-widest text-white">
                        {d.meta.toUpperCase()}
                      </span>
                      <ArrowUpRight
                        size={20}
                        className="opacity-50 group-hover:opacity-100 group-hover:rotate-45 transition-all"
                      />
                    </div>
                    <h3 className="font-display font-900 tracking-tight text-2xl md:text-3xl mt-auto pt-8 leading-none">
                      {d.name}
                    </h3>
                    <p className="font-body text-sm mt-3 text-white/90 leading-relaxed">{d.card}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* further afield cross-link */}
      <section className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14">
          <Link
            to="/beyond-copenhagen"
            data-testid="trips-beyond-link"
            className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white border border-zinc-200 shadow-brut px-6 md:px-8 py-6 hover:-translate-y-0.5 hover:shadow-brut-lg transition-all"
          >
            <div className="flex items-center gap-3">
              <Compass size={22} className="shrink-0 text-[#DF3226]" />
              <p className="font-display font-800 text-xl md:text-2xl tracking-tight">
                Willing to go further?{" "}
                <span className="font-editorial italic font-600 text-[#DF3226]">Odense, Aarhus, Skagen…</span>
              </p>
            </div>
            <span className="shrink-0 font-body font-700 text-sm inline-flex items-center gap-1.5 text-[#DF3226]">
              The Danish gems guide <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        </div>
      </section>

      {/* planner CTA */}
      <section data-testid="trips-cta" className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-24 text-center">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">MAKE A DAY OF IT</p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl mt-4 max-w-2xl mx-auto">
              Slot a trip into <span className="font-editorial italic font-600">your days</span>
            </h2>
            <p className="font-body text-base text-white/80 max-w-xl mx-auto mt-5">
              The day trips are in the planner too — add one to your itinerary and it lands as its own day,
              travel time included.
            </p>
            <Link
              to="/#planner"
              data-scroll="#planner"
              data-testid="trips-cta-planner"
              className="mt-8 inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
            >
              Open the planner
            </Link>
          </Reveal>
        </div>
      </section>

      <GygTourBand tourId="38371" label="MAKE IT A GUIDED DAY TRIP" />

      <ExploreLinks
        testid="explore-trips"
        links={[
          { label: "Further afield — Odense, Aarhus & more", to: "/beyond-copenhagen" },
          { label: "Staying in town? Things to do in Copenhagen", to: "/things-to-do" },
          { label: "Where to base yourself — where to stay", to: "/stay" },
        ]}
      />
      <ShareBar />
    </main>
  );
}
